import {
    DuplicateAction,
    ParentDuplicateRecord
} from '@/shared/api/rest/services/parent-duplicates.service'
import { ChildRecord, ParentRecord } from '@/shared/api/rest/types'

/**
 * Which half of a pair a record is.
 *
 * Detection orders the pair by `_id` and files the first as `parent_id`, so
 * "primary" is stable for a given row but is NOT a claim about which record is
 * better — that is exactly what the reviewer decides.
 */
export type DuplicateSide = 'primary' | 'secondary'

/** The action that keeps the named side and deletes the other. */
export const KEEP_ACTION: Record<DuplicateSide, DuplicateAction> = {
    primary: 'accept',
    secondary: 'keep_duplicate'
}

export const SIDE_LABEL: Record<DuplicateSide, string> = {
    primary: 'Primary record',
    secondary: 'Secondary record'
}

/**
 * Review state derived from the three booleans the backend keeps.
 *
 * `merged` covers both `accept` and `keep_duplicate` — either way one record
 * absorbed the other. `dismissed` is `reject`: both records still exist.
 */
export type DuplicateStatus = 'pending' | 'merged' | 'dismissed'

export const duplicateStatus = (record: ParentDuplicateRecord): DuplicateStatus => {
    if (!record.processed) return 'pending'
    return record.accepted ? 'merged' : 'dismissed'
}

export const STATUS_LABEL: Record<DuplicateStatus, string> = {
    pending: 'Pending review',
    merged: 'Merged',
    dismissed: 'Not a duplicate'
}

export const STATUS_COLOR: Record<DuplicateStatus, string> = {
    pending: 'orange',
    merged: 'green',
    dismissed: 'gray'
}

/** A parent hydrated with the children and branch a list row does not carry. */
export type DuplicateParty = ParentRecord & {
    email?: string | null
    middleName?: string | null
    branch?: { _id?: string; name?: string } | null
}

/**
 * Combine what the duplicates endpoint already gave us with what had to be
 * fetched.
 *
 * `GET /parent-duplicates/*` eager-loads both `parents` rows and the backend
 * batches the `public.users` columns onto them, so `userId`, `branchId`,
 * `relationship`, the address fields, `createdAt`, the name, email, phone and
 * `lastLoginAt` are all in hand the moment the list lands — no request needed.
 * What those rows cannot carry is the child associations; that is what
 * `fetched` supplies. It is the same row plus enrichment, so it spreads last
 * and wins wherever it has a value.
 */
export const resolveParty = (
    embedded?: ParentRecord | null,
    fetched?: DuplicateParty | null
): DuplicateParty | null => {
    if (!embedded && !fetched) return null
    return { ...(embedded ?? {}), ...(fetched ?? {}) } as DuplicateParty
}

/**
 * Attach a resolved branch name when only `branchId` is known — the embedded
 * rows carry the id, and the branch list is a separate cached lookup.
 */
export const withBranchName = (
    party: DuplicateParty | null,
    branchName: (branchId?: string | null) => string
): DuplicateParty | null => {
    if (!party || party.branch?.name || !party.branchId) return party
    return { ...party, branch: { _id: party.branchId, name: branchName(party.branchId) } }
}

export const getPartyName = (party?: DuplicateParty | null): string => {
    const name = [party?.firstName, party?.lastName].filter(Boolean).join(' ').trim()
    return name || party?.email?.trim() || ''
}

/**
 * The name a pair labels itself with, from the rows the listing already sent.
 *
 * Detection pairs on matching name, so either side answers; the second is only
 * consulted because a merged-away side arrives as null. Empty when the server
 * did not enrich the rows — callers fall back to their own lookup.
 */
export const embeddedPartyName = (record: ParentDuplicateRecord): string =>
    getPartyName(record.parent) || getPartyName(record.duplicate)

export const getChildName = (child: ChildRecord): string =>
    [child.firstName, child.middleName, child.lastName].filter(Boolean).join(' ').trim()

/** `similarityScore` arrives as a pg decimal string; `null` if unparseable. */
export const parseScore = (value?: number | string | null): number | null => {
    if (value === null || value === undefined || value === '') return null
    const score = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(score) ? score : null
}

export const formatScore = (value?: number | string | null): string => {
    const score = parseScore(value)
    return score === null ? '—' : `${Math.round(score)}%`
}

export const formatDate = (value?: string | null): string => {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
}

export const formatDateTime = (value?: string | null): string => {
    if (!value) return ''
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString()
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/**
 * How long ago, in words — "yesterday", "3 months ago".
 *
 * A timestamp alone doesn't answer the question the reviewer is actually
 * asking, which is whether an account is still in use: telling 2024 from 2026
 * means reading two dates and subtracting. The relative form is the comparison,
 * already done. Returns '' for anything unusable so callers can fall back.
 */
export const formatRelative = (value?: string | null, now = Date.now()): string => {
    if (!value) return ''
    const time = new Date(value).getTime()
    if (Number.isNaN(time)) return ''

    const elapsed = now - time
    // Clock skew between the server and this browser can put a stamp slightly
    // in the future; "in 4 seconds" reads as a bug, "just now" doesn't.
    if (elapsed < MINUTE_MS) return 'just now'
    if (elapsed < HOUR_MS) {
        const minutes = Math.floor(elapsed / MINUTE_MS)
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
    }
    if (elapsed < DAY_MS) {
        const hours = Math.floor(elapsed / HOUR_MS)
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
    }

    const days = Math.floor(elapsed / DAY_MS)
    if (days === 1) return 'yesterday'
    if (days < 30) return `${days} days ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`
    const years = Math.floor(days / 365)
    return `${years} ${years === 1 ? 'year' : 'years'} ago`
}

/** A timestamp with the elapsed time spelled out after it. */
export const formatDateTimeWithAge = (value?: string | null): string => {
    const stamp = formatDateTime(value)
    if (!stamp) return ''
    const relative = formatRelative(value)
    return relative ? `${stamp} · ${relative}` : stamp
}

/**
 * When this account was last signed into.
 *
 * Three outcomes, and they mean different things: a stamp, `null` for an
 * account that has never been used (which is itself a strong hint about which
 * of two records to keep), and — when the field is absent entirely — '' so the
 * row falls back to a dash rather than claiming the account is unused.
 */
export const formatLastLogin = (value?: string | null): string => {
    if (value === undefined) return ''
    if (value === null) return 'Never signed in'
    return formatDateTimeWithAge(value) || ''
}

/** Short form of a uuid, for telling two otherwise-identical records apart. */
export const shortId = (id?: string | null) => (id ? id.slice(0, 8) : '—')

/** Both parent ids across a set of pairs, de-duplicated. */
export const partyIds = (records: ParentDuplicateRecord[]): string[] => [
    ...new Set(records.flatMap((record) => [record.parentId, record.duplicateId]))
]
